import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import React from "react";

export interface VersionNameModalProps {
  open: boolean;
  value: string;
  initialValue: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onClose: () => void;
}

export const VersionNameModal: React.FC<VersionNameModalProps> = ({
  open,
  value,
  initialValue,
  onChange,
  onSave,
  onClose,
}) => {
  const trimmedValue = value.trim();
  const hasChange = trimmedValue !== initialValue.trim();
  const canSave = trimmedValue.length > 0 && hasChange;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="sm" hideCloseButton className="gap-0 px-6 py-6 sm:px-8 sm:py-8">
        <DialogClose
          type="button"
          className="absolute right-4 top-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg text-neutral-500 transition-colors duration-200 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:shadow-focus"
          aria-label="Close"
        >
          <X size={20} strokeWidth={2} aria-hidden />
        </DialogClose>

        <DialogTitle className="pr-10 text-lg font-bold text-neutral-900 sm:text-xl">
          Edit Version Name
        </DialogTitle>

        <div className="mt-5 sm:mt-6">
          <label htmlFor="version-name-input" className="sr-only">
            Version name
          </label>
          <input
            id="version-name-input"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter version name"
            className="w-full rounded-xl border-0 bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-900 ring-1 ring-neutral-200 transition-colors duration-200 placeholder:text-neutral-500 focus-visible:shadow-focus"
            autoFocus
            aria-invalid={!value.trim()}
            aria-describedby="version-name-hint"
          />
          <DialogDescription id="version-name-hint" className="mt-2 text-xs text-neutral-500">
            Choose a clear name to identify this version.
          </DialogDescription>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:mt-8 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="cursor-pointer rounded-xl border-0 bg-transparent px-5 py-2.5 text-sm font-bold text-neutral-700 ring-1 ring-neutral-200 transition-colors duration-200 hover:bg-neutral-50 hover:ring-neutral-300 focus-visible:shadow-focus"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cursor-pointer rounded-xl border-0 bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors duration-200 hover:bg-primary-hover focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onSave}
            disabled={!canSave}
          >
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
